import { QueryLoader } from "@Light/components/app/QueryLoader";
import { isPastDue, paymentDays } from "@Light/components/dash/Billing/common";
import { createInvoiceLink } from "@Light/components/dash/BillingInvoice/common";
import { Button } from "@Light/components/page/Button";
import { ButtonFooter } from "@Light/components/page/ButtonFooter";
import { Subtitle } from "@Light/components/page/Subtitle";
import { SuccessBox } from "@Light/components/page/errors/SuccessBox";
import {
  useHeaderBackNavigate,
  useHeaderTitle,
} from "@Light/components/page/header/context";
import { useScaffold } from "@Light/scaffold";
import { useLight } from "@Light/services/light";
import type { InvoiceResponse } from "@Light/services/lightTypes";
import dayjs from "dayjs";
import { useState } from "react";

const BACK_ROUTE = "/account/payment-method/";

export type PaymentMethodUpdatedProps = {
  hideBack?: boolean;
  backLink?: string;
  returnLink?: string;
};

export const PaymentMethodUpdated: React.FC<PaymentMethodUpdatedProps> = ({
  hideBack,
  backLink,
  returnLink,
}) => {
  const { useGetBillingInvoicesQuery } = useLight();
  const billingInvoices = useGetBillingInvoicesQuery();

  useHeaderTitle("Payment method updated");
  useHeaderBackNavigate(hideBack ? undefined : backLink || BACK_ROUTE);

  return (
    <QueryLoader queries={[billingInvoices]} allowError>
      <PaymentMethodUpdatedB1
        invoices={billingInvoices.data?.data || []}
        returnLink={returnLink}
      />
    </QueryLoader>
  );
};

export type PaymentMethodUpdatedB1Props = {
  invoices: InvoiceResponse[];
  returnLink?: string;
};

export const PaymentMethodUpdatedB1: React.FC<PaymentMethodUpdatedB1Props> = ({
  invoices,
  returnLink,
}) => {
  const [now] = useState(() => dayjs());
  const pastDueInvoices = invoices.filter((invoice) => {
    const days = paymentDays(now, invoice);
    return isPastDue(days, invoice);
  });
  const scaffold = useScaffold();
  const PageBody = scaffold.page.pageBody;
  const Link = scaffold.system.link;
  return (
    <PageBody
      footer={
        <ButtonFooter>
          {pastDueInvoices.length > 0 ? (
            <>
              <Subtitle>
                It looks like you have an unpaid invoice for{" "}
                <span className="font-medium">${pastDueInvoices[0].total}</span>{" "}
                that was due on{" "}
                <span className="font-medium">
                  {dayjs(pastDueInvoices[0].payment_due_date).format(
                    "MMM D, YYYY",
                  )}
                </span>
                . Click "Pay invoice" below to continue on to pay the invoice.
              </Subtitle>
              <Link
                to={`${createInvoiceLink(pastDueInvoices[0].number)}make-payment`}
                className="w-full"
              >
                <Button size="lg" className="w-full">
                  Pay invoice
                </Button>
              </Link>
            </>
          ) : (
            <Link to={returnLink || BACK_ROUTE} className="w-full">
              <Button size="lg" className="w-full">
                Return
              </Button>
            </Link>
          )}
        </ButtonFooter>
      }
    >
      <div className="flex flex-col gap-4 pt-8">
        <SuccessBox subtitle="You've successfully updated your payment method" />
      </div>
    </PageBody>
  );
};
